"use client";

import { IconGearOutline18, IconMinusOutline18, IconPencilOutline18, IconPlusOutline18 } from "nucleo-ui-outline-18";
import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import type { VersionDiffSummary, VersionChange } from "@/lib/workflow/versionDiff";
import type { Workflow } from "@/stores/workflowsStore";

export function CompareChangesList({ diff }: { diff: VersionDiffSummary }) {
  const nodeChanges = diff.changes?.filter(c => c.category === "node") || [];
  const settingChanges = diff.changes?.filter(c => c.category === "setting") || [];
  const connectionChanges = diff.changes?.filter(c => c.category === "connection") || [];

  const addedNodes = nodeChanges.filter(c => c.type === "added");
  const removedNodes = nodeChanges.filter(c => c.type === "removed");
  const modifiedNodes = nodeChanges.filter(c => c.type === "modified");

  return (
    <div className="space-y-2">
      {addedNodes.map((change, i) => (
        <div key={`added-${i}`} className="flex items-start gap-2 text-sm">
          <IconPlusOutline18 className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
          <span className="text-ds-text-secondary">
            Added{" "}
            <code className="rounded bg-green-100 px-1 py-0.5 font-mono text-xs text-green-700 dark:bg-green-900/30 dark:text-green-400">
              {change.details?.nodeType || "node"}
            </code>
          </span>
        </div>
      ))}
      {removedNodes.map((change, i) => (
        <div key={`removed-${i}`} className="flex items-start gap-2 text-sm">
          <IconMinusOutline18 className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
          <span className="text-ds-text-secondary">
            Removed{" "}
            <code className="rounded bg-red-100 px-1 py-0.5 font-mono text-xs text-red-700 dark:bg-red-900/30 dark:text-red-400">
              {change.details?.nodeType || "node"}
            </code>
          </span>
        </div>
      ))}
      {modifiedNodes.map((change, i) => (
        <div key={`modified-${i}`} className="flex items-start gap-2 text-sm">
          <IconPencilOutline18 className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
          <span className="text-ds-text-secondary">
            Modified{" "}
            <code className="rounded bg-amber-100 px-1 py-0.5 font-mono text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              {change.details?.nodeType || "node"}
            </code>
          </span>
        </div>
      ))}
      {connectionChanges.map((change, i) => (
        <div key={`connection-${i}`} className="flex items-start gap-2 text-sm">
          <IconPencilOutline18 className="mt-0.5 h-4 w-4 flex-shrink-0 text-purple-500" />
          <span className="text-ds-text-secondary">{change.description}</span>
        </div>
      ))}
      {settingChanges.map((change, i) => (
        <div key={`setting-${i}`} className="flex items-start gap-2 text-sm">
          <IconGearOutline18 className="text-ds-text-muted mt-0.5 h-4 w-4 flex-shrink-0" />
          <SettingChangeDisplay change={change} />
        </div>
      ))}
    </div>
  );
}

function SettingChangeDisplay({ change }: { change: VersionChange }) {
  if (change.details?.field === "name") {
    return (
      <span className="text-ds-text-secondary">
        Renamed from{" "}
        <code className="rounded bg-red-100 px-1 py-0.5 font-mono text-xs text-red-700 dark:bg-red-900/30 dark:text-red-400">
          {String(change.details.oldValue)}
        </code>{" "}
        to{" "}
        <code className="rounded bg-green-100 px-1 py-0.5 font-mono text-xs text-green-700 dark:bg-green-900/30 dark:text-green-400">
          {String(change.details.newValue)}
        </code>
      </span>
    );
  }

  if (change.details?.field === "maxRuns") {
    return (
      <span className="text-ds-text-secondary">
        Max runs:{" "}
        <code className="bg-ds-neutral-100 dark:bg-ds-neutral-800 rounded px-1 py-0.5 font-mono text-xs">
          {String(change.details.oldValue)}
        </code>{" "}
        →{" "}
        <code className="bg-ds-neutral-100 dark:bg-ds-neutral-800 rounded px-1 py-0.5 font-mono text-xs">
          {String(change.details.newValue)}
        </code>
      </span>
    );
  }

  if (change.details?.field === "cooldownMs") {
    return (
      <span className="text-ds-text-secondary">
        Cooldown:{" "}
        <code className="bg-ds-neutral-100 dark:bg-ds-neutral-800 rounded px-1 py-0.5 font-mono text-xs">
          {String(change.details.oldValue)}ms
        </code>{" "}
        →{" "}
        <code className="bg-ds-neutral-100 dark:bg-ds-neutral-800 rounded px-1 py-0.5 font-mono text-xs">
          {String(change.details.newValue)}ms
        </code>
      </span>
    );
  }

  return <span className="text-ds-text-secondary">{change.description}</span>;
}

export async function fetchVersionWorkflow(workflowId: string, version: number): Promise<Workflow | null> {
  try {
    const response = await authenticatedFetch<{
      code: number;
      data: Workflow;
    }>(`${V2_ENDPOINT}/v1/workflows/${workflowId}/versions/${version}`);
    if (!response.ok) return null;
    const data = await response.json();
    if (data.code !== 0) return null;
    return data.data;
  } catch {
    return null;
  }
}
